import { customAlphabet } from 'nanoid';

import { ConditionOperator, ConditionsType, DocumentID } from '../common';
import { SupportedMatchInfoHashCategory } from '../userMatchInfoHash';
import { WidgetItemAspectRatio } from '../widgetItemAspectRatio';
import { ContentCollectionDocumentIDNew, PostsFilterType, createContentCollection } from './contentCollections/contentCollection';

export enum WidgetEntryType {
  button = 'button',
  imagePreview = 'imagePreview',
  link = 'link',
  content = 'content',
}

export const WidgetEntryIDNew = 'new';

export const WidgetEntryLocations = ['pdps', 'plps', 'home', 'all'] as const;
export type WidgetEntryLocation = (typeof WidgetEntryLocations)[number];

export const WidgetDisplayOptions = ['all', 'url'] as const;
export type WidgetDisplayOption = (typeof WidgetDisplayOptions)[number];

export type WidgetDisplayCondition = {
  option: WidgetDisplayOption;
  operator: ConditionOperator;
  value: string;
};

export type WidgetDisplayConditionsFilter = {
  type: ConditionsType;
  conditions: WidgetDisplayCondition[];
};

export type WidgetEntryConfiguration = {
  id: string;
  type: WidgetEntryType;
  enabled: boolean;
  injectionScript: string;
  minimumNumberOfVerifiedPosts?: number;
  isThemeInserted: boolean;
  location: WidgetEntryLocation;
  displayFilter?: WidgetDisplayConditionsFilter;
};

export enum WidgetSingleRowLayoutVisibleItems {
  dynamic = 'dynamic',
  dynamicLarge = 'dynamicLarge',
  one = '1',
  two = '2',
  three = '3',
  four = '4',
  five = '5',
  six = '6',
  seven = '7',
}

export const WidgetContentLayoutTypes = ['stories', 'carousel', 'popup', 'page'] as const;
export type WidgetContentLayoutType = (typeof WidgetContentLayoutTypes)[number];

export const WidgetContentLayoutSubTypes = [
  'grid',
  'singleRow',
  'singleRowFixed',
  'singleRowFreeScroll',
  'stories',
  'popup',
  'dynamic',
  'feed',
] as const;
export type WidgetContentLayoutSubType = (typeof WidgetContentLayoutSubTypes)[number];

export interface WidgetCommonLayoutConfiguration {
  visibleItems?: WidgetSingleRowLayoutVisibleItems;
  itemBorderRadius?: number;
  itemBorderWidth?: number;
  itemPadding?: number;
  aspectRatio?: WidgetItemAspectRatio;
  showPreviewText?: boolean;
  showLikes?: boolean;
  autoplayVideo?: boolean;
}

export interface WidgetSingleRowScrollLayoutConfiguration extends WidgetCommonLayoutConfiguration {
  itemSize?: number;
  itemBorderRadius?: number;
  itemPadding?: number;
  carouselInset?: number;
}

export type WidgetSingleRowLayoutConfiguration = WidgetCommonLayoutConfiguration;

export interface WidgetLayoutArrowConfiguration {
  enabled?: boolean;
  buttonSize?: number;
  round?: boolean;
  arrowSize?: number;
}

export enum WidgetFixedRowLayoutConfigurationDefaults {
  itemWidth = 240,
  arrowEnabled = 1,
  arrowButtonSize = 48,
  arrowSize = 16,
  arrowRoundEnabled = 1,
}

export interface WidgetFixedRowLayoutConfiguration extends WidgetCommonLayoutConfiguration {
  itemWidth?: number;
  arrowConfiguration?: WidgetLayoutArrowConfiguration;
  autoplay?: boolean;
  autoplayDelay?: number;
}

export const WidgetBubbleLayoutStyles = ['round', 'square'] as const;
export type WidgetBubbleLayoutStyle = (typeof WidgetBubbleLayoutStyles)[number];

export interface WidgetBubbleLayoutConfiguration {
  style?: WidgetBubbleLayoutStyle;
  aspectRatio?: WidgetItemAspectRatio;
  itemBorderRadius?: number;
  itemSize?: number;
  itemBorderWidth?: number;
  itemShadowEnabled?: boolean;
  autoplayVideo?: boolean;
  scrollBackgroundColor?: string;
}

export interface WidgetStoriesLayoutProps {
  itemPadding?: number;
}

export type WidgetStoriesLayoutConfiguration = WidgetBubbleLayoutConfiguration & WidgetStoriesLayoutProps;

export const HorizontalOptions = ['left', 'right'] as const;
export type HorizontalOption = (typeof HorizontalOptions)[number];

export const VerticalOptions = ['top', 'center', 'bottom'] as const;
export type VerticalOption = (typeof VerticalOptions)[number];

export interface WidgetPopupPosition {
  horizontal: HorizontalOption;
  marginHorizontal: number;
  vertical: VerticalOption;
  marginVertical: number;
}

export interface WidgetPopupTrigger {
  delay: number;
}

export interface WidgetPopupTextBubble {
  enabled: boolean;
  text: string;
}

export interface WidgetPopupLayoutProps {
  position?: WidgetPopupPosition;
  trigger?: WidgetPopupTrigger;
  textBubble?: WidgetPopupTextBubble;
}

export type WidgetPopupLayoutConfiguration = WidgetBubbleLayoutConfiguration & WidgetPopupLayoutProps;

export const ButtonElementTypes = ['anchor', 'button'] as const;
export type ButtonElementType = (typeof ButtonElementTypes)[number];

// No need to extend this interface as we don't add any properties
export type WidgetFeedLayoutConfiguration = WidgetCommonLayoutConfiguration;

export interface WidgetGridLayoutConfiguration extends WidgetCommonLayoutConfiguration {
  numberOfRows?: number;
  loadMoreCSSStyle?: string;
  loadMoreButtonType?: ButtonElementType;
}

export interface WidgetButtonEntryConfiguration extends WidgetEntryConfiguration {
  cssClasses: string;
  cssStyle: string;
  useAlternativeButtonTitle?: boolean;
}

export interface WidgetImagePreviewEntryConfiguration extends WidgetEntryConfiguration {
  blurRadius?: number;
  nonInteractive?: boolean;
  buttonStyle?: string;
  descriptionStyle?: string;
}

export interface WidgetLinkEntryConfiguration extends WidgetEntryConfiguration {
  prefix: string;
  linkText: string;
}

export enum WidgetBubbleLayoutConfigurationDefaults {
  itemSize = 128,
  itemBorderWidth = 3,
  itemShadowEnabled = 1,
  itemPadding = 18,
  style = 'round',
  scrollBgColor = '#fff',
}

export type WidgetContentLayout = WidgetSingleRowScrollLayoutConfiguration &
  WidgetSingleRowLayoutConfiguration &
  WidgetGridLayoutConfiguration &
  WidgetFeedLayoutConfiguration &
  WidgetStoriesLayoutConfiguration &
  WidgetFixedRowLayoutConfiguration &
  WidgetPopupLayoutConfiguration;

export interface WidgetContentEntryConfigurationLayout extends WidgetContentLayout {
  layoutSubType: WidgetContentLayoutSubType;
}

export interface WidgetContentEntryConfigurationProps {
  name: string;
  contentCollectionDocID: DocumentID;
  layoutType: WidgetContentLayoutType;
  layoutSubType: WidgetContentLayoutSubType;
  category?: SupportedMatchInfoHashCategory;
  mobileLayout?: WidgetContentEntryConfigurationLayout;
  filter: PostsFilterType;
  hasManualPostsOrder?: boolean;
}

export type WidgetContentEntryConfiguration = WidgetEntryConfiguration & WidgetContentEntryConfigurationProps & WidgetContentLayout;

export type WidgetAddToCartConfiguration = {
  enabled: boolean;
  getAvailableSizesScript: string;
  addToCartScript: string;
  goToCartURL?: string;
  reloadPage?: boolean;
};

export type WidgetConfiguration = {
  enabled: boolean;
  buttonColor: string;
  buttonRadius?: number;
  font?: string;
  useiFrame?: boolean;
  hideVesticoLogo?: boolean;
  hideGenderSelection?: boolean;
  entries: WidgetEntryConfiguration[];
  likesEnabled?: boolean;
  useInlineSizeRecommendation?: boolean;
  showSizeChartWithoutBodyInfo?: boolean;
  addToCartConfig?: WidgetAddToCartConfiguration;
  productNameFilterRegex?: string;
};

const isValidLayout = (layout?: WidgetContentLayout) => {
  if (!layout) {
    return true;
  }
  const { itemBorderRadius, itemPadding, itemSize, visibleItems, itemShadowEnabled, itemBorderWidth } = layout;

  return (
    (itemBorderRadius === undefined || typeof itemBorderRadius === 'number') &&
    (itemPadding === undefined || typeof itemPadding === 'number') &&
    (itemSize === undefined || typeof itemSize === 'number') &&
    (visibleItems === undefined || typeof visibleItems.valueOf() === 'string') &&
    (typeof itemBorderWidth === 'undefined' || typeof itemBorderWidth === 'number') &&
    (typeof itemShadowEnabled === 'undefined' || typeof itemShadowEnabled === 'boolean')
  );
};

export const widgetEntryConfigurationIsValid = (entryConfiguration: WidgetEntryConfiguration) => {
  const isValid =
    typeof entryConfiguration.id === 'string' &&
    typeof entryConfiguration.enabled === 'boolean' &&
    typeof entryConfiguration.injectionScript === 'string' &&
    typeof entryConfiguration.type.valueOf() === 'string';

  if (!isValid) {
    return false;
  }

  const isValidLink = () => {
    const linkEntry = entryConfiguration as WidgetLinkEntryConfiguration;
    return typeof linkEntry.linkText === 'string' && typeof linkEntry.prefix === 'string';
  };

  const isValidButton = () => {
    const buttonEntry = entryConfiguration as WidgetButtonEntryConfiguration;
    // Manually fix css style as it was introduced later
    if (!buttonEntry.cssStyle) {
      buttonEntry.cssStyle = '';
    }
    return typeof buttonEntry.cssClasses === 'string' && typeof buttonEntry.cssStyle === 'string';
  };

  const isValidImagePreview = () => {
    const imagePreviewEntry = entryConfiguration as WidgetImagePreviewEntryConfiguration;
    return (
      (typeof imagePreviewEntry.buttonStyle === 'undefined' || typeof imagePreviewEntry.buttonStyle === 'string') &&
      (typeof imagePreviewEntry.descriptionStyle === 'undefined' || typeof imagePreviewEntry.descriptionStyle === 'string')
    );
  };

  const isValidContenEntry = () => {
    const carouselEntry = entryConfiguration as WidgetContentEntryConfiguration;
    return !carouselEntry.layoutSubType || (typeof carouselEntry.layoutSubType.valueOf() === 'string' && isValidLayout(carouselEntry));
  };

  const { type } = entryConfiguration;
  switch (type) {
    case WidgetEntryType.link:
      return isValidLink();
    case WidgetEntryType.button:
      return isValidButton();
    case WidgetEntryType.imagePreview:
      return isValidImagePreview();
    case WidgetEntryType.content:
      return isValidContenEntry();
    default:
      throw new Error(`Unhandled type ${type}`);
  }
};

export const widgetConfigurationIsValid = (widgetConfiguration: WidgetConfiguration) => {
  const isValid =
    typeof widgetConfiguration.enabled === 'boolean' &&
    typeof widgetConfiguration.buttonColor === 'string' &&
    (widgetConfiguration.buttonRadius === undefined || typeof widgetConfiguration.buttonRadius === 'number') &&
    (widgetConfiguration.font === undefined || typeof widgetConfiguration.font === 'string') &&
    (widgetConfiguration.useiFrame === undefined || typeof widgetConfiguration.useiFrame === 'boolean') &&
    (widgetConfiguration.hideVesticoLogo === undefined || typeof widgetConfiguration.hideVesticoLogo === 'boolean') &&
    (widgetConfiguration.likesEnabled === undefined || typeof widgetConfiguration.likesEnabled === 'boolean') &&
    (widgetConfiguration.hideGenderSelection === undefined || typeof widgetConfiguration.hideGenderSelection === 'boolean') &&
    (widgetConfiguration.addToCartConfig === undefined ||
      (typeof widgetConfiguration.addToCartConfig.enabled === 'boolean' &&
        (typeof widgetConfiguration.addToCartConfig.reloadPage === 'boolean' || widgetConfiguration.addToCartConfig.reloadPage === undefined) &&
        typeof widgetConfiguration.addToCartConfig.getAvailableSizesScript === 'string' &&
        typeof widgetConfiguration.addToCartConfig.addToCartScript === 'string'));
  const entriesAreValid =
    widgetConfiguration.entries.length === 0 ||
    widgetConfiguration.entries.map((config) => widgetEntryConfigurationIsValid(config)).reduce((prev, current) => prev && current);
  return isValid && entriesAreValid;
};

export const getTitleForWidgetEntryType = (entryType: WidgetEntryType) => {
  switch (entryType) {
    case WidgetEntryType.button:
      return 'Button';
    case WidgetEntryType.link:
      return 'Link';
    case WidgetEntryType.imagePreview:
      return 'Image preview';
    case WidgetEntryType.content:
      return 'Widget';
  }
  throw new Error(`Unknown type ${entryType}`);
};

export const populateEntryConfigForLayout = (entryConfiguration: WidgetContentLayout, layoutType: WidgetContentLayoutSubType) => {
  switch (layoutType) {
    case 'dynamic':
    case 'feed':
    case 'grid':
    case 'singleRow':
    case 'singleRowFreeScroll':
    case 'stories':
      {
        const config = entryConfiguration as WidgetStoriesLayoutConfiguration;
        config.itemSize = WidgetBubbleLayoutConfigurationDefaults.itemSize;
        config.itemBorderWidth = WidgetBubbleLayoutConfigurationDefaults.itemBorderWidth;
        config.itemShadowEnabled = WidgetBubbleLayoutConfigurationDefaults.itemShadowEnabled === 1;
        config.style = WidgetBubbleLayoutConfigurationDefaults.style;
        config.itemPadding = WidgetBubbleLayoutConfigurationDefaults.itemPadding;
      }
      break;
    case 'popup':
      {
        const config = entryConfiguration as WidgetContentLayout;
        config.itemSize = WidgetBubbleLayoutConfigurationDefaults.itemSize;
        config.itemBorderWidth = WidgetBubbleLayoutConfigurationDefaults.itemBorderWidth;
        config.itemShadowEnabled = WidgetBubbleLayoutConfigurationDefaults.itemShadowEnabled === 1;
        config.style = WidgetBubbleLayoutConfigurationDefaults.style;

        config.position = {
          horizontal: 'right',
          marginHorizontal: 30,
          vertical: 'bottom',
          marginVertical: 40,
        };
        config.textBubble = {
          enabled: false,
          text: '',
        };
        config.trigger = {
          delay: 0,
        };
      }
      break;
    case 'singleRowFixed':
      if (!entryConfiguration.arrowConfiguration) {
        entryConfiguration.arrowConfiguration = {};
      }
      break;
    default:
      // skipcq JS-0378
      throw new Error(`Unhandled carousel layout type ${layoutType}`);
  }
};

export const titleForWidgetEntryLocation = (loc: WidgetEntryLocation) => {
  const map: { [key in WidgetEntryLocation]: string } = {
    home: 'Home page',
    plps: 'Collection pages',
    pdps: 'Product pages',
    all: 'All pages',
  };
  return map[loc];
};

export const titleForLayoutType = (type: WidgetContentLayoutType) => {
  const map: { [key in WidgetContentLayoutType]: string } = {
    carousel: 'Carousel',
    page: 'Feed',
    popup: 'Popup',
    stories: 'Stories',
  };
  return map[type];
};

export const titleForLayoutSubType = (type: WidgetContentLayoutSubType) => {
  const map: { [key in WidgetContentLayoutSubType]: string } = {
    singleRow: 'Single Row',
    singleRowFreeScroll: 'Single Row: free scroll',
    singleRowFixed: 'Single Row: fixed width',
    grid: 'Grid',
    dynamic: 'Collage',
    feed: 'Feed',
    popup: 'Popup',
    stories: 'Stories',
  };
  return map[type];
};

const getDefaultLayoutSubTypeForType = (type: WidgetContentLayoutType) => {
  const map: { [key in WidgetContentLayoutType]: WidgetContentLayoutSubType } = {
    carousel: 'singleRowFixed',
    page: 'feed',
    popup: 'popup',
    stories: 'stories',
  };
  return map[type];
};

export const getLayoutSubTypesForType = (type: WidgetContentLayoutType) => {
  const map: { [key in WidgetContentLayoutType]: WidgetContentLayoutSubType[] } = {
    carousel: ['singleRowFixed', 'singleRow', 'singleRowFreeScroll', 'grid'],
    page: ['feed', 'dynamic'],
    popup: ['popup'],
    stories: ['stories'],
  };
  return map[type];
};

export const createWidgetContentEntryConfigurationID = () => {
  const firstCharAlphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  const alphabet = `0123456789${firstCharAlphabet}`;
  const firstChar = customAlphabet(firstCharAlphabet, 1)();
  const id = customAlphabet(alphabet, 23)();
  return `${firstChar}${id}`;
};

export const createWidgetContentEntryConfiguration = (widgetConfiguration: WidgetConfiguration, layoutType: WidgetContentLayoutType) => {
  const id = createWidgetContentEntryConfigurationID();
  const subType = getDefaultLayoutSubTypeForType(layoutType);
  const defaultConfiguration: WidgetContentEntryConfiguration = {
    id,
    name: `${titleForLayoutType(layoutType)}: Widget #${widgetConfiguration.entries.length}`,
    location: 'pdps',
    type: WidgetEntryType.content,
    enabled: subType !== 'popup',
    injectionScript: '',
    layoutType,
    layoutSubType: subType,
    showPreviewText: true,
    showLikes: true,
    isThemeInserted: true,
    autoplayVideo: true,
    contentCollectionDocID: ContentCollectionDocumentIDNew,
    filter: createContentCollection('').postsFilter.type,
  };

  populateEntryConfigForLayout(defaultConfiguration, subType);

  return defaultConfiguration;
};

export const isContentOnlyEntry = (entry: WidgetEntryConfiguration) => {
  return ![WidgetEntryType.button, WidgetEntryType.link, WidgetEntryType.imagePreview].includes(entry.type);
};

export const getContentOnlyEntries = (widgetConfiguration: WidgetConfiguration) => {
  return widgetConfiguration.entries.filter((e) => isContentOnlyEntry(e)) as WidgetContentEntryConfiguration[];
};

export const titleForWidgetDisplayOption = (option: WidgetDisplayOption) => {
  const options: { [key in WidgetDisplayOption]: string } = {
    all: 'Show on all pages',
    url: 'URL',
  };
  return options[option];
};

const isMatchingCondition = (condition: WidgetDisplayCondition, testValue: string) => {
  const { option, operator, value: rawValue } = condition;

  if (option === 'all' || !rawValue) {
    return true;
  }

  const value = rawValue.trim();

  if (value.length === 0) {
    return true;
  }

  const operatorMap: { [key in ConditionOperator]: () => RegExp } = {
    contain: () => new RegExp(`${value}`, 'i'),
    endWith: () => new RegExp(`${value}$`, 'i'),
    equal: () => new RegExp(`^${value}$`, 'i'),
    startWith: () => new RegExp(`^${value}`, 'i'),
    notEqual: () => new RegExp(`^(?!${value}).*$`, 'i'),
    notContain: () => new RegExp(`^((?!${value}).)*$`, 'i'),
  };
  const regex = operatorMap[operator]();
  const result = regex.test(testValue);
  return result;
};

const isMatchingConditions = (conditions: WidgetDisplayCondition[], testValue: string) => {
  return conditions.map((cond) => isMatchingCondition(cond, testValue)).reduce((a, b) => a || b, false);
};

export const displayFilterIsMatching = (displayFilter: WidgetDisplayConditionsFilter | undefined, value: string) => {
  if (!displayFilter) {
    return true;
  }
  const { type, conditions } = displayFilter;

  switch (type) {
    case 'all':
      return isMatchingConditions(conditions, value);
    case 'any':
      return conditions.map((cond) => isMatchingConditions([cond], value)).reduce((a, b) => a || b, false);
  }
  console.error(`Missing handing display filter type ${type}`);
  return true;
};
